import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { camelizeKeys } from 'humps';
import * as PropTypes from 'prop-types';
/**/
import { load } from '../../action/history';
import { getHistorySync } from '../../command/history';
import { useCore } from '../../context/core';
import {
  selectHistoryIsListingLoading,
  selectHistoryIsListingNeedsFetch,
  selectHistoryListings,
} from '../../reducer/history';
import ListingCard from './../Listing/ListingCard';
import { Swiper, SwiperSlide } from 'swiper/react';
import HistorySkeletonWrapper from '../../ui-components/Skeleton/HistorySkeleton';
import Skeleton from '../../ui-components/Skeleton/Skeleton';
import {
  getListingDetailsCache,
  setListingDetailsCache,
  checkAndClearCacheIfLanguageChanged,
} from '../../command/listingCache';
import EmptyState from '../EmptyState/EmptyState';
import { TRANSLATIONS } from '../../../translations';

const HistoryList = (props) => {
  const dispatch = useDispatch();
  const { endpoints } = useCore();
  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const isHistoryLoading = useSelector(selectHistoryIsListingLoading);
  const needsFetch = useSelector(selectHistoryIsListingNeedsFetch);
  const listingIds = useSelector(selectHistoryListings);
  const isFetching = isLoading || isHistoryLoading;
  const [areListingsResolved, setAreListingsResolved] = useState(false);
  // A visitor with nothing stored can be answered on the first render, with no
  // skeleton in between.
  const [hasStoredHistory] = useState(() => getHistorySync().length > 0);

  // load listing history
  useEffect(() => {
    if (!needsFetch || isHistoryLoading) return;
    dispatch(load());
  }, [needsFetch, isHistoryLoading]);

  // Load listing details with caching
  useEffect(() => {
    const fetchDetails = async () => {
      if (listingIds.length === 0) {
        setListings([]);
        setAreListingsResolved(true);
        return;
      }

      setIsLoading(true);

      const cacheCleared = await checkAndClearCacheIfLanguageChanged();

      const cache = await getListingDetailsCache();
      const now = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000;

      // Determine which listings need fetching
      const toFetch = cacheCleared
        ? listingIds
        : listingIds.filter((id) => {
          const cached = cache[id];
          return !cached || now - cached.timestamp > twentyFourHours;
        });

      let freshData = [];
      if (toFetch.length > 0) {
        try {
          const res = await axios.get(`${endpoints.core}/listings`, {
            params: {
              id: toFetch,
              locale: window.freedomeConfig.locale,
            },
          });
          freshData = camelizeKeys(res.data).map((listing) => ({
            ...listing,
          }));

          // Update cache with fresh data
          const updatedCache = { ...cache };
          freshData.forEach((listing) => {
            updatedCache[listing.id] = {
              details: listing,
              timestamp: now,
            };
          });
          // Ensure cache only contains current history IDs
          const finalCache = {};
          listingIds.forEach((id) => {
            if (updatedCache[id]) {
              finalCache[id] = updatedCache[id];
            }
          });
          await setListingDetailsCache(finalCache);
        } catch (error) {
          console.error('Could not load the listings in the history', error);
        }
      }

      // Combine cached and fresh data
      const allData = listingIds.map((id) => {
        if (toFetch.includes(id)) {
          return freshData.find((listing) => listing && listing.id === id);
        }
        return cache[id]?.details;
      });

      // Filter out undefined listings
      const existingData = allData
        .filter((listing) => listing)
        .filter(
          (listing) =>
            listing.id !== 0 && listing.id !== null && listing.id !== undefined
        );

      setListings(existingData);
      setAreListingsResolved(true);
      setIsLoading(false);
    };
    fetchDetails();
  }, [listingIds]);

  // Empty means no listing resolved, not no id stored: an id whose listing was
  // unpublished, or one the lookup could not reach, resolves to nothing.
  const isResolved =
    !hasStoredHistory || (!needsFetch && !isFetching && areListingsResolved);
  const isEmpty = isResolved && listings.length === 0;

  if (props.isHomepage && !listings.length) {
    return null;
  }

  if (props.isHomepage) {
    return isFetching ? (
      <div className="content__section padding-top--6 margin-bottom--3">
        <div className="grid-x grid-padding-x align-center">
          <div className="cell xlarge-10">
            <div className="content__section__title--secondary d-flex justify-center">
              <Skeleton classNames={'radius-xs'} height={16} width={176} />
            </div>
          </div>
        </div>
        <HistorySkeletonWrapper showAsSwiper={props.showAsSwiper} />
      </div>
    ) : (
      <section className="content__section content__section-resizable">
        <div className="grid-container">
          <div className="grid-x grid-padding-x align-center">
            <div className="cell large-10">
              <h2 className="content__section__title--secondary">
                {app_main_wpml.continue_watching}
              </h2>
            </div>
          </div>

          <div className="grid-x grid-padding-x align-center">
            <div className="cell xlarge-10">
              {listings.slice(0, props.cardsToShow).map((listing, index) => (
                <ListingCard
                  cardsCompact={props.cardsCompact}
                  key={listing.id}
                  position={index}
                  listType="history-list"
                  showSummary={true}
                  {...listing}
                  renderNightStayBadge={listing.showBundleBadge}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (props.showAsSwiper) {
    return isFetching ? (
      <HistorySkeletonWrapper showAsSwiper={true} />
    ) : (
      <Swiper spaceBetween={8} slidesPerView={1.1}>
        {listings.slice(0, props.cardsToShow).map((listing, index) => (
          <SwiperSlide key={listing.id}>
            <ListingCard
              cardsCompact={props.cardsCompact}
              position={index}
              listType="history-list"
              className={props.itemClassName}
              {...listing}
              renderNightStayBadge={listing.showBundleBadge}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    );
  }

  if (!isResolved) {
    return <HistorySkeletonWrapper />;
  }

  if (isEmpty) {
    return (
      <EmptyState
        sources={[
          `${endpoints.bucket}images/history/history-empty.webm`,
          `${endpoints.bucket}images/history/history-empty.mp4`,
        ]}
        titleLead={TRANSLATIONS.HISTORY_EMPTY_TITLE_LEAD}
        titleRest={TRANSLATIONS.HISTORY_EMPTY_TITLE_REST}
        text={TRANSLATIONS.HISTORY_EMPTY_TEXT}
        ctaLabel={TRANSLATIONS.EMPTY_STATE_CTA}
        replaces="[data-history-filled]"
      />
    );
  }

  return (
    <div className={props.className}>
      {listings.slice(0, props.cardsToShow).map((listing, index) => (
        <ListingCard
          cardsCompact={props.cardsCompact}
          key={listing.id}
          position={index}
          listType="history-list"
          className={props.itemClassName}
          {...listing}
          renderNightStayBadge={listing.showBundleBadge}
        />
      ))}
    </div>
  );
};

HistoryList.sanitizeProps = (props) => {
  const cardsToShow = parseInt(props.cardsToShow, 10);

  return {
    ...props,
    isHomepage: props.isHomepage === 'true',
    cardsCompact: props.cardsCompact
      ? props.cardsCompact === 'true'
      : undefined,
    cardsToShow: cardsToShow > 0 ? cardsToShow : undefined,
  };
};

HistoryList.propTypes = {
  cardsCompact: PropTypes.bool.isRequired,
  isHomepage: PropTypes.bool.isRequired,
  cardsToShow: PropTypes.number,
  itemClassName: PropTypes.string,
  className: PropTypes.string,
};
HistoryList.defaultProps = {
  cardsCompact: true,
  isHomepage: false,
  cardsToShow: Infinity,
  itemClassName: '',
  className: '',
};

export default HistoryList;
